import runtime from 'serviceworker-webpack-plugin/lib/runtime';
import gql from 'graphql-tag';
import NotificationService from '@/services/NotificationService';
import retry from 'retry-as-promised';
import StorageService from '@/services/StorageService';
import { apolloClient } from '@/apollo';

const sendSubscriptionData = async (subscription) => {
  const data = subscription.toJSON();
  const notifications = JSON.parse(StorageService.get('notifications'));
  const enabledNotifications = Object.keys(notifications)
    .map(key => ({ key, value: notifications[key] }))
    .filter(value => value.value)
    .map(value => value.key);

  await apolloClient.mutate({
    mutation: gql`mutation ($data: NotificationRegister!){
      registerNotification(data: $data)
    }`,
    variables: {
      data: {
        endpoint: data.endpoint,
        auth: data.keys.auth,
        p256dh: data.keys.p256dh,
        acceptedNotifications: enabledNotifications,
      },
    },
  });
};

const updateSubscription = async () => {
  const worker = await navigator.serviceWorker.getRegistration();
  const subsciption = await worker.pushManager.getSubscription();
  await sendSubscriptionData(subsciption);
};

const urlBase64ToUint8Array = (base64String) => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');

  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }

  return outputArray;
};

const subscribeUser = async (registration) => {
  try {
    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(process.env.VUE_APP_VAPID_PUBLIC_KEY),
    });

    await sendSubscriptionData(subscription);
  } catch (e) {
    throw e;
  }
};


const register = async () => {
  if ('serviceWorker' in navigator) {
    if (Notification.permission !== 'granted') {
      return;
    }
    try {
      const registration = await runtime.register();
      setTimeout(() => subscribeUser(registration), 1000);
    } catch (e) {
      throw new Error('Service worker registration failed:', e);
    }
  }
};

const initialize = async () => {
  if ('serviceWorker' in navigator) {
    const existingPermission = Notification.permission;
    if (existingPermission !== 'granted') {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        return false;
      }
      await register();
    }

    return true;
  }

  return null;
};

const sendNotification = async (notification, date) => {
  const { data } = await retry(() => apolloClient.mutate({
    mutation: gql`mutation NotificationQuery($notification: String!, $date: TimelessDate){
      notify(notification: $notification, date: $date)
    }`,
    variables: {
      notification,
      date,
    },
  }), {
    max: 3,
    backoffExponent: 1.2,
  });

  return data.notify;
};

const notifyFridayAdded = async (date) => {
  try {
    await sendNotification('fridayAdded', date);
    NotificationService.showSnackbar({
      message: 'mainComponent.notifiedUsers',
      timeout: 5000,
      position: 'topRight',
    });
  } catch (error) {
    NotificationService.showAlert({
      message: 'mainComponent.notificationsError',
      level: 'error',
    });
  }
};

const notifyLunchArrived = async () => {
  try {
    await sendNotification('lunchArrived');

    NotificationService.showSnackbar({
      message: 'mainComponent.notifiedUsers',
      timeout: 5000,
      position: 'topRight',
    });
  } catch (error) {
    NotificationService.showAlert({
      message: 'mainComponent.notificationsError',
      level: 'error',
    });
  }
};

const notifyLunchRate = async () => {
  try {
    await sendNotification('orderRating');

    NotificationService.showSnackbar({
      message: 'mainComponent.notifiedUsers',
      timeout: 5000,
      position: 'topRight',
    });
  } catch (error) {
    NotificationService.showAlert({
      message: 'mainComponent.notificationsError',
      level: 'error',
    });
  }
};

const notifyBalanceReminder = async () => {
  try {
    await sendNotification('balanceReminder');

    NotificationService.showSnackbar({
      message: 'mainComponent.notifiedUsers',
      timeout: 5000,
      position: 'topRight',
    });
  } catch (error) {
    NotificationService.showAlert({
      message: 'mainComponent.notificationsError',
      level: 'error',
    });
  }
};

export default {
  initialize,
  register,
  updateSubscription,
  notifyLunchArrived,
  notifyFridayAdded,
  notifyLunchRate,
  notifyBalanceReminder,
};
